import { useCallback, useState } from "react"
import { AutoProcessController } from "../preview-files/AutoProcessController"
import store from "integrations/redux/store"
import { ImageProcessingState } from "@nova/constants"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"
import { InspectorPanelKeyValueToggle } from "features/map/inspector-panel/InspectorPanelKeyValueToggle"
import { useUploadFlowContext } from "panels/content/side-panel/upload-flow/useUploadFlowContext"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"

type AutoProcessToggleProps = {
    process: () => Promise<void> | void
    forceEnable?: boolean
}

export const AutoProcessToggle = ({ process, forceEnable }: AutoProcessToggleProps) => {
    // if isProcessing, shut down component
    const {
        flightId,
        selectUploadsForSet,
        selectUploadStateSummary,
        minUploads,
        selectFlightById,
        updateFlight,
    } = useUploadFlowContext()
    const dispatch = useAppDispatch()
    const flight = useAppSelector<{ processAfterUpload?: boolean }>(selectFlightById(flightId))
    const [isProcessing, setIsProcessing] = useState(false)

    useEffectUnsafe(() => {
        AutoProcessController.removeAllListeners(`flightId:${flightId}:uploaded-all`)
        AutoProcessController.removeAllListeners(`flightId:${flightId}:ingest-progress`)
        if (!(flight.processAfterUpload || forceEnable) || isProcessing) return
        let isCleanedUp = false

        const tryProcess = () => {
            const state = store.getState()
            const uploads = selectUploadsForSet(flightId)(state)
            const summary = selectUploadStateSummary(flightId)(state)
            const allIngestDone =
                (uploads?.length || 0) >= minUploads &&
                !summary.errorCount &&
                summary.stateCount &&
                summary.stateCount[ImageProcessingState.IngestDone] === uploads?.length
            if (allIngestDone) {
                process()
                if (!isCleanedUp) setIsProcessing(true)
                AutoProcessController.removeAllListeners(`flightId:${flightId}:uploaded-all`)
                AutoProcessController.removeAllListeners(`flightId:${flightId}:ingest-progress`)
            }
        }

        AutoProcessController.on(`flightId:${flightId}:uploaded-all`, tryProcess)
        AutoProcessController.on(`flightId:${flightId}:ingest-progress`, tryProcess)

        return () => {
            isCleanedUp = true
        }
    }, [process, flight.processAfterUpload, isProcessing, forceEnable])

    const setToggleChecked = useCallback(
        (isToggled: boolean) => {
            dispatch(updateFlight({ id: flightId, updates: { processAfterUpload: isToggled } }))
        },
        [dispatch, updateFlight, flightId]
    )

    return (
        <InspectorPanelKeyValueToggle
            className="my-4"
            toggleClassName="justify-between"
            isToggled={forceEnable || !!flight.processAfterUpload}
            setIsToggled={setToggleChecked}
            keyTitle="Process after uploading"
            disabled={forceEnable}
        />
    )
}
